import type { DailyBar, RuleStep } from "../types";

export function pct(value: number, digits = 1): string {
  return `${value >= 0 ? "+" : ""}${value.toFixed(digits)}%`;
}

export function money(value: number, currency: "USD" | "KRW" = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(value);
}

export function number(value: number, digits = 2): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(value);
}

export function buildLadder(params: {
  referenceHigh: number;
  triggerDropPct: number;
  ladderCount: number;
  spacingPctPoint: number;
  spacingBasis: "prior-high" | "previous-ladder";
  direction: "CP" | "RCP";
  budgets: number[];
}): RuleStep[] {
  const sign = params.direction === "CP" ? -1 : 1;
  const steps: RuleStep[] = [];
  let previousLevel = params.referenceHigh;

  for (let index = 0; index <= params.ladderCount; index += 1) {
    const isTrigger = index === 0;
    const dropPct = params.triggerDropPct + (isTrigger ? 0 : params.spacingPctPoint * index);
    const level =
      params.spacingBasis === "prior-high"
        ? params.referenceHigh * (1 + (sign * dropPct) / 100)
        : isTrigger
          ? params.referenceHigh * (1 + (sign * dropPct) / 100)
          : previousLevel * (1 + (sign * params.spacingPctPoint) / 100);

    previousLevel = level;
    const actualMovePct = (level / params.referenceHigh - 1) * 100;
    steps.push({
      id: isTrigger ? "trigger" : `step-${index}`,
      label: isTrigger ? "Trigger / Step 1" : `Step ${index + 1}`,
      dropPct: actualMovePct,
      indexLevel: level,
      budget: params.budgets[index] ?? 0,
      status: isTrigger ? "armed" : "pending"
    });
  }

  return steps;
}

export function weightedAverageIndex(records: Array<{ level: number; amount: number }>): number {
  const totalAmount = records.reduce((sum, record) => sum + record.amount, 0);
  if (totalAmount === 0) return 0;
  return records.reduce((sum, record) => sum + record.level * record.amount, 0) / totalAmount;
}

export function monitoringMetrics(history: DailyBar[], current: number) {
  if (!history.length) {
    return {
      rangeHigh: current,
      rangeLow: current,
      rangeHighDate: "",
      rangeLowDate: "",
      drawdownFromRangeHighPct: 0,
      reboundFromRangeLowPct: 0,
      high10y: current,
      low10y: current,
      high10yDate: "",
      low10yDate: "",
      drawdownFrom10YHighPct: 0,
      reboundFrom10YLowPct: 0,
      rangePositionPct: 0
    };
  }

  const highBar = history.reduce((max, bar) => (bar.close > max.close ? bar : max), history[0]);
  const lowBar = history.reduce((min, bar) => (bar.close < min.close ? bar : min), history[0]);
  const range = highBar.close - lowBar.close;

  return {
    rangeHigh: highBar.close,
    rangeLow: lowBar.close,
    rangeHighDate: highBar.date,
    rangeLowDate: lowBar.date,
    drawdownFromRangeHighPct: (current / highBar.close - 1) * 100,
    reboundFromRangeLowPct: (current / lowBar.close - 1) * 100,
    high10y: highBar.close,
    low10y: lowBar.close,
    high10yDate: highBar.date,
    low10yDate: lowBar.date,
    drawdownFrom10YHighPct: (current / highBar.close - 1) * 100,
    reboundFrom10YLowPct: (current / lowBar.close - 1) * 100,
    rangePositionPct: range === 0 ? 0 : ((current - lowBar.close) / range) * 100
  };
}
